import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Sanitize text for PDF output - remove characters not supported by Helvetica
 * Keeps Latin, Latin Extended, numbers, punctuation, and common symbols
 */
function sanitizeText(text) {
  if (!text) return '';
  // Keep ASCII, Latin-1 Supplement, Latin Extended-A/B, and common punctuation
  // Replace unsupported characters (CJK, etc.) with empty string
  return text.replace(/[^\u0000-\u024F\u1E00-\u1EFF\u2000-\u206F\u2070-\u209F\u20A0-\u20CF\u2100-\u214F]/g, '').trim();
}

const COLORS = {
  primary: [30, 41, 59], // slate-800
  secondary: [37, 99, 235], // blue-600
  accent: [6, 182, 212], // cyan-500
  success: [16, 185, 129], // emerald-500
  muted: [100, 116, 139], // slate-500
  border: [226, 232, 240], // slate-200
  background: [248, 250, 252], // slate-50
  white: [255, 255, 255],
};

// Standard font settings for consistency
const FONTS = {
  body: { size: 9, style: 'normal' },
  small: { size: 8, style: 'normal' },
  label: { size: 9, style: 'bold' },
  heading: { size: 11, style: 'bold' },
};

function formatDate(date) {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Clean company name by removing parenthetical product references
 * e.g., "Character Technologies, Inc. (Character.ai)" -> "Character Technologies, Inc."
 */
function cleanCompanyName(name) {
  if (!name) return '';
  // Remove parenthetical content at the end (often product name duplicated)
  return name.replace(/\s*\([^)]*\)\s*$/, '').trim();
}

const DISCLAIMER_TEXT = 'This data is AI-generated and may contain errors. For analyst-validated data and custom research services, upgrade to AskAIQ Premium. Contact sales@askaiq.com';

/**
 * Export research to PDF
 * @param {Object} research - The research data
 * @param {'tree' | 'matrix'} type - Export type
 */
export async function exportToPDF(research, type) {
  const pdf = new jsPDF({
    orientation: type === 'matrix' ? 'landscape' : 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;
  let yPos = margin;

  // Header
  pdf.setFillColor(...COLORS.primary);
  pdf.rect(0, 0, pageWidth, 35, 'F');

  pdf.setTextColor(...COLORS.white);
  pdf.setFontSize(24);
  pdf.setFont('helvetica', 'bold');
  pdf.text('AskAIQ', margin, 15);

  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Market Research Report', margin, 22);

  pdf.setFontSize(8);
  pdf.text(formatDate(research.createdAt), pageWidth - margin, 15, { align: 'right' });

  yPos = 45;

  // Market Info
  pdf.setTextColor(...COLORS.primary);
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.text(sanitizeText(research.marketName) || 'Market Research', margin, yPos);
  yPos += 8;

  pdf.setTextColor(...COLORS.muted);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  const definitionLines = pdf.splitTextToSize(sanitizeText(research.marketDefinition), pageWidth - margin * 2);
  pdf.text(definitionLines, margin, yPos);
  yPos += definitionLines.length * 5 + 5;

  // Stats bar
  pdf.setFillColor(...COLORS.background);
  pdf.roundedRect(margin, yPos, pageWidth - margin * 2, 12, 2, 2, 'F');

  pdf.setTextColor(...COLORS.primary);
  pdf.setFontSize(9);
  const stats = [
    `${research.companies.length} Companies`,
    `${research.companies.reduce((sum, c) => sum + c.products.length, 0)} Products`,
    `${research.capabilities.length} Capabilities`,
    `${research.features.length} Features`,
  ];
  const statWidth = (pageWidth - margin * 2) / stats.length;
  stats.forEach((stat, i) => {
    pdf.text(stat, margin + statWidth * i + statWidth / 2, yPos + 7.5, { align: 'center' });
  });
  yPos += 18;

  // Add disclaimer
  pdf.setTextColor(...COLORS.muted);
  pdf.setFontSize(7);
  pdf.setFont('helvetica', 'italic');
  const disclaimerLines = pdf.splitTextToSize(DISCLAIMER_TEXT, pageWidth - margin * 2);
  pdf.text(disclaimerLines, margin, yPos);
  yPos += disclaimerLines.length * 3 + 5;

  if (type === 'tree') {
    exportTreeView(pdf, research, margin, yPos, pageWidth, pageHeight);
  } else {
    exportMatrixView(pdf, research, margin, yPos);
  }

  // Footer on all pages
  const pageCount = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setFillColor(...COLORS.border);
    pdf.rect(0, pageHeight - 10, pageWidth, 10, 'F');
    pdf.setTextColor(...COLORS.muted);
    pdf.setFontSize(8);
    pdf.text('Generated by AskAIQ - askiaiq.com', margin, pageHeight - 4);
    pdf.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageHeight - 4, { align: 'right' });
  }

  pdf.save(`${sanitizeText(research.marketName) || 'market-research'}-${type}.pdf`);
}

function exportTreeView(pdf, research, margin, startY, pageWidth, pageHeight) {
  let yPos = startY;
  const indent = 8;
  const maxWidth = pageWidth - margin * 2;
  const lineHeight = 4.5;

  const checkNewPage = (requiredSpace = 20) => {
    if (yPos + requiredSpace > pageHeight - 15) {
      pdf.addPage();
      yPos = margin;
      return true;
    }
    return false;
  };

  // Helper to set consistent body text style
  const setBodyText = () => {
    pdf.setTextColor(...COLORS.primary);
    pdf.setFontSize(FONTS.body.size);
    pdf.setFont('helvetica', FONTS.body.style);
  };

  // Helper to set consistent label style
  const setLabelText = (color = COLORS.primary) => {
    pdf.setTextColor(...color);
    pdf.setFontSize(FONTS.label.size);
    pdf.setFont('helvetica', FONTS.label.style);
  };

  research.companies.forEach((company) => {
    checkNewPage(40);

    // Company header
    pdf.setFillColor(...COLORS.secondary);
    pdf.roundedRect(margin, yPos, maxWidth, 10, 2, 2, 'F');
    pdf.setTextColor(...COLORS.white);
    pdf.setFontSize(FONTS.heading.size);
    pdf.setFont('helvetica', FONTS.heading.style);
    pdf.text(sanitizeText(company.name), margin + 4, yPos + 7);
    if (company.url) {
      pdf.setFontSize(FONTS.small.size);
      pdf.setFont('helvetica', FONTS.small.style);
      pdf.text(sanitizeText(company.url), pageWidth - margin - 4, yPos + 7, { align: 'right' });
    }
    yPos += 14;

    // Company description
    if (company.description) {
      checkNewPage(15);
      pdf.setTextColor(...COLORS.muted);
      pdf.setFontSize(FONTS.body.size);
      pdf.setFont('helvetica', FONTS.body.style);
      const descLines = pdf.splitTextToSize(sanitizeText(company.description), maxWidth - indent);
      pdf.text(descLines, margin + indent, yPos);
      yPos += descLines.length * lineHeight + 3;
    }

    // Company metadata
    const meta = [];
    if (company.founders) meta.push(`Founders: ${sanitizeText(company.founders)}`);
    if (company.funding) meta.push(`Funding: ${sanitizeText(company.funding)}`);
    if (company.revenue) meta.push(`Revenue: ${sanitizeText(company.revenue)}`);
    if (meta.length > 0) {
      checkNewPage(10);
      pdf.setTextColor(...COLORS.muted);
      pdf.setFontSize(FONTS.small.size);
      pdf.setFont('helvetica', FONTS.small.style);
      pdf.text(meta.join('  •  '), margin + indent, yPos);
      yPos += 6;
    }

    // Products
    company.products.forEach((product) => {
      checkNewPage(30);

      // Product header - calculate height based on whether we have pricing
      const productBoxHeight = product.pricing ? 14 : 8;
      pdf.setFillColor(...COLORS.accent);
      pdf.roundedRect(margin + indent, yPos, maxWidth - indent, productBoxHeight, 1, 1, 'F');

      // Product name
      pdf.setTextColor(...COLORS.white);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text(sanitizeText(product.name), margin + indent + 4, yPos + 5.5);

      // Pricing on second line if present
      if (product.pricing) {
        pdf.setFontSize(FONTS.small.size);
        pdf.setFont('helvetica', FONTS.small.style);
        // Truncate pricing if too long
        const maxPricingWidth = maxWidth - indent - 8;
        let pricingText = sanitizeText(product.pricing);
        while (pdf.getTextWidth(pricingText) > maxPricingWidth && pricingText.length > 20) {
          pricingText = pricingText.slice(0, -4) + '...';
        }
        pdf.text(pricingText, margin + indent + 4, yPos + 11);
      }
      yPos += productBoxHeight + 3;

      // Product description
      if (product.description) {
        checkNewPage(12);
        pdf.setTextColor(...COLORS.muted);
        pdf.setFontSize(FONTS.body.size);
        pdf.setFont('helvetica', FONTS.body.style);
        const prodDescLines = pdf.splitTextToSize(sanitizeText(product.description), maxWidth - indent * 2);
        pdf.text(prodDescLines, margin + indent * 2, yPos);
        yPos += prodDescLines.length * lineHeight + 2;
      }

      // Capabilities
      if (product.capabilityIds.length > 0) {
        checkNewPage(15);
        setLabelText(COLORS.secondary);
        pdf.text('Capabilities:', margin + indent * 2, yPos);
        yPos += 5;

        product.capabilityIds.forEach((capId) => {
          const cap = research.capabilities.find((c) => c.id === capId);
          if (cap) {
            checkNewPage(8);
            setBodyText();
            pdf.text(`- ${sanitizeText(cap.name)}`, margin + indent * 3, yPos);
            yPos += lineHeight;
          }
        });
        yPos += 2;
      }

      // Features
      if (product.featureIds.length > 0) {
        checkNewPage(15);
        setLabelText(COLORS.success);
        pdf.text('Features:', margin + indent * 2, yPos);
        yPos += 5;

        product.featureIds.forEach((featId) => {
          const feat = research.features.find((f) => f.id === featId);
          if (feat) {
            checkNewPage(8);
            setBodyText();
            pdf.text(`- ${sanitizeText(feat.name)}`, margin + indent * 3, yPos);
            yPos += lineHeight;
          }
        });
        yPos += 2;
      }

      yPos += 3;
    });

    yPos += 5;
  });
}

function exportMatrixView(pdf, research, margin, startY) {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  // Flatten products (these become columns)
  const products = research.companies.flatMap((company) =>
    company.products.map((product) => ({
      ...product,
      companyName: cleanCompanyName(company.name),
    }))
  );

  // Combine capabilities and features as rows
  const rows = [
    ...research.capabilities.map((c) => ({ ...c, type: 'capability' })),
    ...research.features.map((f) => ({ ...f, type: 'feature' })),
  ];

  // Handle empty data
  if (products.length === 0 || rows.length === 0) {
    pdf.setTextColor(...COLORS.muted);
    pdf.setFontSize(12);
    pdf.text('No data available for matrix view', margin, startY);
    return;
  }

  // Calculate how many product columns fit per page
  const firstColumnWidth = 50; // Fixed width for capability/feature names
  const productColumnWidth = 22; // Width for each product column
  const availableWidth = pageWidth - margin * 2 - firstColumnWidth;
  const productsPerPage = Math.floor(availableWidth / productColumnWidth);
  const totalProducts = products.length;

  // Split products into batches
  const batches = [];
  for (let i = 0; i < totalProducts; i += productsPerPage) {
    batches.push(products.slice(i, i + productsPerPage));
  }

  let currentY = startY;

  batches.forEach((batchProducts, batchIndex) => {
    const startIdx = batchIndex * productsPerPage;
    const endIdx = startIdx + batchProducts.length;

    // Add new page for subsequent batches
    if (batchIndex > 0) {
      pdf.addPage();
      currentY = margin;
    }

    // Batch header
    pdf.setTextColor(...COLORS.primary);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`Products ${startIdx + 1} to ${endIdx} (of ${totalProducts})`, margin, currentY);
    currentY += 6;

    // Build table data for this batch
    const tableBody = rows.map((row) => {
      const rowData = [sanitizeText(row.name)];
      batchProducts.forEach((product) => {
        if (row.type === 'capability') {
          rowData.push(product.capabilityIds?.includes(row.id) ? 'Y' : '');
        } else {
          rowData.push(product.featureIds?.includes(row.id) ? 'Y' : '');
        }
      });
      return rowData;
    });

    // Build column styles for product columns
    const columnStyles = {
      0: {
        halign: 'left',
        fontStyle: 'bold',
        cellWidth: firstColumnWidth,
      },
    };
    batchProducts.forEach((_, idx) => {
      columnStyles[idx + 1] = { cellWidth: productColumnWidth };
    });

    // Create table using autoTable
    autoTable(pdf, {
      startY: currentY,
      head: [['Capability / Feature', ...batchProducts.map((p) => ({
        content: `${sanitizeText(p.companyName)}\n${sanitizeText(p.name)}`,
        styles: {}
      }))]],
      body: tableBody,
      theme: 'grid',
      styles: {
        fontSize: 6,
        cellPadding: 1.5,
        halign: 'center',
        valign: 'middle',
        font: 'helvetica',
        overflow: 'linebreak',
      },
      headStyles: {
        fillColor: COLORS.primary,
        textColor: COLORS.white,
        fontStyle: 'normal',
        fontSize: 5,
        halign: 'center',
        minCellHeight: 12,
      },
      columnStyles,
      alternateRowStyles: {
        fillColor: COLORS.background,
      },
      didParseCell: (data) => {
        // Style check marks
        if (data.cell.raw === 'Y') {
          data.cell.styles.textColor = COLORS.success;
          data.cell.styles.fontStyle = 'bold';
        }
        // Color code row headers based on type
        if (data.section === 'body' && data.column.index === 0) {
          const row = rows[data.row.index];
          if (row.type === 'capability') {
            data.cell.styles.textColor = COLORS.secondary;
          } else {
            data.cell.styles.textColor = COLORS.accent;
          }
        }
        // Make product name bold in header (second line)
        if (data.section === 'head' && data.column.index > 0) {
          // Product name is on second line - handled via content styling
        }
      },
      didDrawCell: (data) => {
        // Draw product name in bold on second line of header cells
        if (data.section === 'head' && data.column.index > 0) {
          const product = batchProducts[data.column.index - 1];
          if (product) {
            const cellX = data.cell.x;
            const cellY = data.cell.y;
            const cellWidth = data.cell.width;
            const cellHeight = data.cell.height;

            // Clear existing text
            pdf.setFillColor(...COLORS.primary);
            pdf.rect(cellX + 0.5, cellY + 0.5, cellWidth - 1, cellHeight - 1, 'F');

            // Draw company name (normal)
            pdf.setTextColor(...COLORS.white);
            pdf.setFontSize(5);
            pdf.setFont('helvetica', 'normal');
            const companyLines = pdf.splitTextToSize(sanitizeText(product.companyName), cellWidth - 2);
            let textY = cellY + 3;
            companyLines.forEach((line) => {
              pdf.text(line, cellX + cellWidth / 2, textY, { align: 'center' });
              textY += 2.5;
            });

            // Draw product name (bold)
            pdf.setFont('helvetica', 'bold');
            const productLines = pdf.splitTextToSize(sanitizeText(product.name), cellWidth - 2);
            productLines.forEach((line) => {
              pdf.text(line, cellX + cellWidth / 2, textY, { align: 'center' });
              textY += 2.5;
            });
          }
        }
      },
      margin: { left: margin, right: margin },
    });

    // Add disclaimer after each table
    const finalY = pdf.lastAutoTable.finalY || currentY + 50;
    if (finalY + 15 < pageHeight - 15) {
      pdf.setTextColor(...COLORS.muted);
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'italic');
      const disclaimerLines = pdf.splitTextToSize(DISCLAIMER_TEXT, pageWidth - margin * 2);
      pdf.text(disclaimerLines, margin, finalY + 5);
    }
  });
}
